import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Link,
    CircularProgress
} from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Legend,
    Tooltip
} from 'recharts';
import { reportService } from '../../../services/api';
import { tokens } from '../../../theme';

const ComplianceChart = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [complianceData, setComplianceData] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchComplianceData = async () => {
            try {
                setLoading(true);
                const response = await reportService.getComplianceReport({
                    page: 0,
                    limit: 1000 // Get all records for accurate counts
                });

                if (response.data.success) {
                    // Calculate totals
                    const totals = response.data.data.reduce((acc, course) => {
                        acc.completed += parseInt(course.completedCount) || 0;
                        acc.pending += parseInt(course.pendingCount) || 0;
                        acc.overdue += parseInt(course.overdueCount) || 0;
                        return acc;
                    }, { completed: 0, pending: 0, overdue: 0 });

                    const total = totals.completed + totals.pending + totals.overdue;

                    // Convert to percentages. The status colours from the theme (N5 pt 31): completed activeText, pending
                    // warnText, overdue alertText. Removed 2 Nov 2026 (N5 pt 31): the primary colour, a light blue
                    // (#8ec5ff) and a red (#EA4335) typed in here.
                    const data = [
                        { 
                            name: 'Completed', 
                            value: Math.round((totals.completed / total) * 100) || 0,
                            color: tokens.color.status.activeText,
                            count: totals.completed
                        },
                        { 
                            name: 'Pending', 
                            value: Math.round((totals.pending / total) * 100) || 0,
                            color: tokens.color.status.warnText,
                            count: totals.pending
                        },
                        { 
                            name: 'Overdue', 
                            value: Math.round((totals.overdue / total) * 100) || 0,
                            color: tokens.color.status.alertText,
                            count: totals.overdue
                        }
                    ];

                    setComplianceData(data);
                }
            } catch (error) {
                console.error('Error fetching compliance data:', error);
                setError('Failed to load compliance data');
            } finally {
                setLoading(false);
            }
        };

        fetchComplianceData();
    }, []);

    const handleViewDetails = () => {
        navigate('/reports/compliance');
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        p: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        boxShadow: 1
                    }}
                >
                    <Typography variant="body2" fontWeight="medium">
                        {payload[0].name}
                    </Typography>
                    <Typography variant="body2" color="primary">
                        {`${payload[0].value}% (${payload[0].payload.count})`}
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    const renderLegend = (props) => {
        const { payload } = props;
        
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    gap: 3,
                    mt: 2
                }}
            >
                {payload.map((entry, index) => (
                    <Box 
                        key={`legend-${index}`}
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: entry.color
                            }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {`${entry.value}% (${entry.payload.count})`}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    if (loading) {
        return (
            <Card>
                <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress />
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent>
                    <Typography color="error">{error}</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Compliance Status
                    </Typography>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={handleViewDetails}
                        sx={{ textDecoration: 'none' }}
                    >
                        View Details
                    </Link>
                </Box>
                <Box sx={{ position: 'relative', height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={complianceData}
                                //innerRadius={40}
                                //outerRadius={100}
                                paddingAngle={2}
                                dataKey="value"
                            >
                                {complianceData.map((entry, index) => (
                                    <Cell 
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend 
                                content={renderLegend}
                                verticalAlign="bottom"
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center'
                        }}
                    >
                        {/*
                        <Typography variant="h4" color="primary" fontWeight="bold">
                            {complianceData[0]?.value || 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Compliant
                        </Typography>
                        */}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default ComplianceChart;